import { Controller } from "@hotwired/stimulus"

// The ⋯ menu behind every message card. One panel on the body, same reason
// snooze_popover cannot reuse dropdown_controller: the cards live inside
// .thread__scroll, which would clip a descendant menu.
const VIEWPORT_MARGIN = 8
const TRIGGER_GAP = 6
const TRIGGER_SELECTOR = "[data-message-menu]"

const VISIBILITY = {
  reply: "messageMenuReply",
  "reply-all": "messageMenuReplyAll",
  forward: "messageMenuForward",
  "forward-new": "messageMenuForwardNew",
  source: "messageMenuSource",
  download: "messageMenuDownload",
  comment: true
}

export default class extends Controller {
  connect() {
    this.openFromEvent = this.openFromEvent.bind(this)
    this.closeOnEscape = this.closeOnEscape.bind(this)
    this.closeOnScroll = this.closeOnScroll.bind(this)
    this.onKeydown = this.onKeydown.bind(this)

    document.addEventListener("click", this.openFromEvent)
    document.addEventListener("keydown", this.closeOnEscape)
    document.addEventListener("scroll", this.closeOnScroll, true)
  }

  disconnect() {
    this.close()
    document.removeEventListener("click", this.openFromEvent)
    document.removeEventListener("keydown", this.closeOnEscape)
    document.removeEventListener("scroll", this.closeOnScroll, true)
  }

  openFromEvent(event) {
    const trigger = event.target.closest?.(TRIGGER_SELECTOR)
    if (trigger) {
      event.preventDefault()
      this.toggle(trigger)
      return
    }

    const item = event.target.closest?.("[data-message-menu-item]")
    if (item && this.element.contains(item)) {
      this.activate(item)
      return
    }

    if (!this.element.hidden && !this.element.contains(event.target)) this.close()
  }

  toggle(trigger) {
    if (this.trigger === trigger && !this.element.hidden) {
      this.close()
      return
    }

    this.open(trigger)
  }

  open(trigger) {
    this.trigger = trigger
    this.applyVisibility(trigger)
    this.wireInspect(trigger)
    this.element.hidden = false
    this.position(trigger)
    this.markExpanded(true)
    this.ignoreScroll = true
    this.firstItem?.focus({ preventScroll: true })
    requestAnimationFrame(() => { this.ignoreScroll = false })
    this.element.addEventListener("keydown", this.onKeydown)
  }

  close() {
    if (this.element.hidden) return

    this.element.hidden = true
    this.markExpanded(false)
    this.element.removeEventListener("keydown", this.onKeydown)
    this.trigger = null
  }

  closeOnEscape(event) {
    if (event.key !== "Escape" || this.element.hidden) return

    const trigger = this.trigger
    this.close()
    trigger?.focus({ preventScroll: true })
  }

  closeOnScroll() {
    if (this.ignoreScroll) return

    this.close()
  }

  onKeydown(event) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return

    event.preventDefault()
    const items = this.visibleItems
    if (!items.length) return

    const current = items.indexOf(document.activeElement)
    const offset = event.key === "ArrowDown" ? 1 : -1
    const next = items[(current + offset + items.length) % items.length]
    next.focus()
  }

  activate(item) {
    const kind = item.dataset.messageMenuItem
    // close() drops this.trigger, so the card we opened from has to travel
    // with the action — compose and comment both need that button's data.
    const trigger = this.trigger
    this.close()
    trigger?.focus({ preventScroll: true })
    if (!trigger) return

    if (kind === "comment") {
      this.commentOn(trigger)
      return
    }
    if (kind === "source" || kind === "download") return

    this.compose(kind, trigger)
  }

  compose(kind, trigger) {
    const composer = this.replyComposer(trigger)
    if (!composer) return

    const parentMessageId = trigger.dataset.messageId || ""
    this.forwardRequest = null

    if (kind === "reply") {
      composer.composeFromMessage({
        to: trigger.dataset.replyTo || "",
        parentMessageId
      })
      return
    }
    if (kind === "reply-all") {
      composer.composeFromMessage({
        to: trigger.dataset.replyAllTo || "",
        cc: trigger.dataset.replyAllCc || "",
        parentMessageId
      })
      return
    }
    if (kind === "forward" || kind === "forward-new") this.forward(kind, trigger, composer, parentMessageId)
  }

  // The forwarded quote is fetched when Forward is picked rather than shipped in every card
  // (FRO-132). A later pick supersedes one still in flight, so a slow response cannot overwrite
  // whatever the agent opened after it. A failed fetch still opens the composer, empty, which is
  // what a card with no body always did.
  async forward(kind, trigger, composer, parentMessageId) {
    const request = this.forwardRequest = Symbol("forward")
    const bodyHtml = await this.fetchForwardBody(trigger.dataset.forwardBodyUrl)
    if (this.forwardRequest !== request) return

    composer.composeFromMessage({
      to: "",
      bodyHtml,
      parentMessageId,
      asNewConversation: kind === "forward-new",
      replaceBody: true
    })
  }

  async fetchForwardBody(url) {
    if (!url) return ""

    try {
      const response = await fetch(url, { headers: { Accept: "text/html" }, credentials: "same-origin" })
      // A redirect is a signed-out session landing on the sign-in page, not a forward body.
      return response.ok && !response.redirected ? await response.text() : ""
    } catch {
      return ""
    }
  }

  commentOn(trigger) {
    const root = trigger.closest("[data-controller~='comment-anchor']")
    const form = root?.querySelector("#comment_composer [data-controller~='composer']")
      || root?.querySelector("#comment_composer form")
    if (!form) return

    const composer = this.application.getControllerForElementAndIdentifier(form, "composer")
    composer?.anchorTo({
      messageId: trigger.dataset.commentMessageId || "",
      excerpt: trigger.dataset.commentExcerpt || ""
    })
  }

  threadRepliable(trigger) {
    return trigger.closest("[data-thread-reply-repliable-value]")
      ?.dataset.threadReplyRepliableValue === "true"
  }

  replyComposer(trigger) {
    const root = trigger?.closest(".thread")?.querySelector("#reply_composer")
    const form = root?.querySelector("[data-controller~='composer']") || root?.querySelector("form")
    if (!form) return null

    return this.application.getControllerForElementAndIdentifier(form, "composer")
  }

  applyVisibility(trigger) {
    this.element.querySelectorAll("[data-message-menu-item]").forEach((item) => {
      const kind = item.dataset.messageMenuItem
      if (kind.endsWith("-divider")) {
        item.hidden = true
        return
      }
      const flag = VISIBILITY[kind]
      if (flag === true) {
        item.hidden = false
        return
      }
      const offered = trigger.dataset[flag] === "true"
      const write = [ "messageMenuReply", "messageMenuReplyAll", "messageMenuForward",
                      "messageMenuForwardNew" ].includes(flag)
      item.hidden = !(offered && (!write || this.threadRepliable(trigger)))
    })
    this.syncDivider("inspect-divider", [ "source", "download" ])
    this.syncDivider("comment-divider", [ "comment" ])
  }

  syncDivider(name, after) {
    const divider = this.element.querySelector(`[data-message-menu-item='${name}']`)
    if (!divider) return

    const visibleAfter = after.some((kind) => {
      const item = this.element.querySelector(`[data-message-menu-item='${kind}']`)
      return item && !item.hidden
    })
    const visibleBefore = this.visibleItems.some((item) => {
      const kind = item.dataset.messageMenuItem
      return kind !== "comment" && !after.includes(kind)
    })
    divider.hidden = !(visibleBefore && visibleAfter)
  }

  wireInspect(trigger) {
    const source = this.element.querySelector("[data-message-menu-item='source']")
    if (source) source.href = trigger.dataset.sourceUrl || "#"
    const download = this.element.querySelector("[data-message-menu-item='download']")
    if (download) download.href = trigger.dataset.downloadUrl || "#"
  }

  position(trigger) {
    const anchor = trigger.getBoundingClientRect()
    const panel = this.element.getBoundingClientRect()

    const below = anchor.bottom + TRIGGER_GAP
    const top = below + panel.height > window.innerHeight - VIEWPORT_MARGIN
      ? anchor.top - panel.height - TRIGGER_GAP
      : below

    const rightAligned = anchor.right - panel.width
    const rightmost = window.innerWidth - panel.width - VIEWPORT_MARGIN
    const left = Math.max(VIEWPORT_MARGIN, Math.min(rightAligned, rightmost))

    this.element.style.top = `${Math.max(VIEWPORT_MARGIN, top)}px`
    this.element.style.left = `${left}px`
  }

  markExpanded(open) {
    document.querySelectorAll(`${TRIGGER_SELECTOR}[aria-expanded]`).forEach((button) => {
      button.setAttribute("aria-expanded", button === this.trigger && open ? "true" : "false")
    })
  }

  get firstItem() {
    return this.visibleItems[0]
  }

  get visibleItems() {
    return [ ...this.element.querySelectorAll("[role='menuitem']") ].filter((item) => !item.hidden)
  }
}
